import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Question, DeviceMobile } from "@phosphor-icons/react";

import Header from "@/components/Header";
import HeadlineTicker from "@/components/HeadlineTicker";
import PipelineIndicator from "@/components/PipelineIndicator";
import VideoSidebar from "@/components/VideoSidebar";
import ClaimCard from "@/components/ClaimCard";
import FactCheckerToggle from "@/components/FactCheckerToggle";
import { isFactCheckerSource } from "@/lib/factCheckers";
import { isSupportedUrl } from "@/lib/youtube";
import useFactCheckStream from "@/hooks/useFactCheckStream";

/**
 * SharePage — endpoint for the PWA Web Share Target.
 * Receives ?url=... (or ?text=... containing a URL) from Android's share sheet
 * and immediately fires the fact-check pipeline.
 */
export default function SharePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { state, start, reset } = useFactCheckStream();
  const [autoStarted, setAutoStarted] = useState(false);
  const [notice, setNotice] = useState("");
  const [factCheckersOnly, setFactCheckersOnly] = useState(false);

  const extractUrl = useCallback((raw) => {
    if (!raw) return null;
    // Some apps send the URL inside `text` (e.g. "Check this out https://youtube.com/...")
    const m = raw.match(/(https?:\/\/[^\s]+)/);
    return m ? m[1] : (raw.trim().startsWith("http") ? raw.trim() : null);
  }, []);

  const sharedUrl = useMemo(() => {
    // Prefer explicit url param, then text, then title
    const candidates = [params.get("url"), params.get("text"), params.get("title")];
    for (const c of candidates) {
      const u = extractUrl(c);
      if (u && isSupportedUrl(u)) return u;
    }
    return null;
  }, [params, extractUrl]);

  useEffect(() => {
    if (autoStarted) return;
    if (!sharedUrl) {
      setNotice(
        "No supported video URL was found in the shared content. Open the app and paste a video URL directly."
      );
      // Redirect home after a short pause
      const t = setTimeout(() => navigate("/"), 3500);
      return () => clearTimeout(t);
    }
    setAutoStarted(true);
    reset();
    setTimeout(() => start(sharedUrl, false), 60);
    toast("Received shared video — starting fact-check", { duration: 2500 });
  }, [sharedUrl, autoStarted, start, reset, navigate]);

  const orderedClaims = useMemo(() => {
    const verdicts = state.verdictsByClaimId;
    const list = (state.claims || []).map((c) => {
      const v = verdicts[c.id];
      if (v) return v;
      return {
        claim_id: c.id,
        original_quote: c.original_quote,
        normalized_claim: c.normalized_claim,
        verdict: "UNVERIFIABLE",
        confidence: 0,
        sources: [],
        short_explanation: "",
        detailed_reasoning: "",
        timestamp_in_video: c.timestamp_in_video,
        priority: c.priority,
        _pending: true,
      };
    });
    Object.values(verdicts).forEach((v) => {
      if (!list.find((x) => x.claim_id === v.claim_id)) list.push(v);
    });
    return list;
  }, [state.claims, state.verdictsByClaimId]);

  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900 relative">
      <Header />
      <HeadlineTicker />

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-10 pb-4 relative z-10">
        <div
          data-testid="share-hero"
          className="flex items-center gap-3 mb-4 font-mono-plex text-[10px] font-semibold text-zinc-500"
        >
          <DeviceMobile weight="fill" className="w-3.5 h-3.5 text-emerald-600" />
          SHARED FROM YOUR VIDEO APP · PIPELINE RUNNING
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tight text-zinc-950 mb-4">
          On it.
        </h1>
        {sharedUrl && (
          <p className="text-zinc-700 font-mono-plex text-[11px] normal-case tracking-normal break-all">
            {sharedUrl}
          </p>
        )}
        {notice && (
          <div
            data-testid="share-notice"
            className="mt-6 border-2 border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 text-sm"
          >
            {notice} Redirecting home…
          </div>
        )}
      </section>

      <AnimatePresence>
        {state.running && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="max-w-[1600px] mx-auto w-full px-6 md:px-12 mb-4"
          >
            <PipelineIndicator stage={state.stage} message={state.stageMessage} active={state.running} />
          </motion.div>
        )}
      </AnimatePresence>

      {state.error && (
        <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 mb-4">
          <div
            data-testid="share-error"
            className="border-2 border-red-500 bg-red-50 text-red-900 px-4 py-3 flex items-start gap-3"
          >
            <span className="font-mono-plex text-[10px] font-semibold flex-shrink-0 mt-0.5">
              PIPELINE HALTED
            </span>
            <span className="text-[13px] font-medium">{state.error}</span>
          </div>
        </div>
      )}

      {(state.metadata || orderedClaims.length > 0) && (
        <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <VideoSidebar
                metadata={state.metadata}
                videoUrl={state.result?.video_url}
                overallVerdict={state.overallVerdict}
                overallConfidence={state.overallConfidence}
                overallSummary={state.overallSummary}
                cached={state.cached}
                processingMs={state.processingMs}
                transcriptExcerpt={state.transcriptExcerpt}
                cacheKey={state.result?.cache_key}
              />
            </div>
          </div>
          <div className="lg:col-span-8 flex flex-col gap-6">
            {orderedClaims.length === 0 && !state.running && state.metadata && (
              <div className="border border-zinc-300 bg-white p-8">
                <Question weight="duotone" className="w-8 h-8 text-zinc-500 mb-3" />
                <h3 className="font-display text-2xl text-zinc-950 mb-2">
                  No verifiable claims detected
                </h3>
                <p className="text-zinc-700">
                  Nothing independently verifiable in this video&apos;s transcript.
                </p>
              </div>
            )}
            {orderedClaims.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 justify-between mb-1">
                <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 flex items-center gap-3">
                  <span>FINDINGS · {orderedClaims.length} CLAIM{orderedClaims.length === 1 ? "" : "S"}</span>
                  {state.running && (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full pulse-dot" />
                      STREAMING
                    </span>
                  )}
                </div>
                <FactCheckerToggle
                  enabled={factCheckersOnly}
                  onChange={setFactCheckersOnly}
                  matchCount={orderedClaims.reduce(
                    (n, c) => n + (c.sources || []).filter(isFactCheckerSource).length,
                    0
                  )}
                  totalCount={orderedClaims.reduce((n, c) => n + (c.sources?.length || 0), 0)}
                />
              </div>
            )}
            <AnimatePresence>
              {orderedClaims.map((c, i) => (
                <ClaimCard key={c.claim_id} claim={c} index={i} factCheckersOnly={factCheckersOnly} />
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}
    </div>
  );
}
